import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, CardBody, CardHeader, Heading, SimpleGrid, FormControl, FormLabel,
  Input, Select, Checkbox, CheckboxGroup, Stack, HStack, VStack, Text, Badge,
  Divider, Button, Wrap, WrapItem, Tag, TagLabel, TagCloseButton, Spinner,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, Alert, AlertIcon, Tooltip,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import newsletterService from '../services/newsletterService';

// Translation keys; translated where they render (language can change at runtime).
const VIP_LABELS = {
  0: 'audienceFilter.vip.regular',
  1: 'audienceFilter.vip.silver',
  2: 'audienceFilter.vip.gold',
  3: 'audienceFilter.vip.platinum',
  4: 'audienceFilter.vip.diamond',
};

const MONTHS = [
  'audienceFilter.months.january', 'audienceFilter.months.february',
  'audienceFilter.months.march', 'audienceFilter.months.april',
  'audienceFilter.months.may', 'audienceFilter.months.june',
  'audienceFilter.months.july', 'audienceFilter.months.august',
  'audienceFilter.months.september', 'audienceFilter.months.october',
  'audienceFilter.months.november', 'audienceFilter.months.december',
];

// Ready-made segments. Each one is just a filter spec — picking a preset
// replaces the working filters wholesale, so the operator can then tweak it.
// labelKey is translated where the preset button renders.
const PRESETS = [
  { id: 'all', labelKey: 'audienceFilter.presets.everyone', filters: {} },
  {
    id: 'past', labelKey: 'audienceFilter.presets.past',
    filters: { minStays: 1 },
  },
  {
    id: 'repeat', labelKey: 'audienceFilter.presets.repeat',
    filters: { minStays: 2 },
  },
  {
    id: 'lapsed', labelKey: 'audienceFilter.presets.lapsed',
    filters: { minStays: 1, lastStayTo: isoMonthsAgo(6) },
  },
  {
    id: 'recent', labelKey: 'audienceFilter.presets.recent',
    filters: { lastStayFrom: isoMonthsAgo(1) },
  },
  {
    id: 'upcoming', labelKey: 'audienceFilter.presets.upcoming',
    filters: { hasUpcomingStay: true },
  },
  {
    id: 'vip', labelKey: 'audienceFilter.presets.vip',
    filters: { vipLevels: [1, 2, 3, 4] },
  },
  {
    id: 'never', labelKey: 'audienceFilter.presets.never',
    filters: { neverStayed: true },
  },
];

function isoMonthsAgo(months) {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 10);
}

const EMPTY = {};

/**
 * Guest targeting panel.
 *
 * Owns nothing: `filters` comes in, `onChange` goes out. The live recipient
 * count is debounced and fetched from the backend so the number shown is the
 * number that will actually be mailed — including the opted-out / no-email
 * deductions, which are the usual reason a count looks "wrong".
 */
const AudienceFilterBuilder = ({ filters, onChange, showPreviewTable = true }) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState(null);
  const [summary, setSummary] = useState(null);
  const [sample, setSample] = useState([]);
  const [counting, setCounting] = useState(false);
  const [countError, setCountError] = useState('');
  const debounceRef = useRef(null);

  const f = filters || EMPTY;
  const set = (key, value) => {
    const next = { ...f };
    // Drop empty values so the saved spec stays readable and `{}` really
    // means "everyone" rather than "a dozen blank keys".
    if (value === '' || value === null || value === undefined ||
        value === false || (Array.isArray(value) && value.length === 0)) {
      delete next[key];
    } else {
      next[key] = value;
    }
    onChange(next);
  };

  useEffect(() => {
    newsletterService.audienceOptions()
      .then(setOptions)
      .catch(() => setOptions({ nationalities: [], languages: [], genders: [], bookingSources: [], roomTypes: [] }));
  }, []);

  // Debounced live count — typing in a date field should not fire a request
  // per keystroke.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setCounting(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await newsletterService.previewAudience(f);
        setSummary(res);
        setSample(res.sample || []);
        setCountError('');
      } catch (err) {
        setCountError(err.response?.data?.detail || t('audienceFilter.errors.countFailed'));
        setSummary(null);
        setSample([]);
      } finally {
        setCounting(false);
      }
    }, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(f)]);

  const activeCount = useMemo(() => Object.keys(f).length, [f]);

  return (
    <VStack align="stretch" spacing={4}>
      {/* Presets */}
      <Card shadow="sm" borderWidth="1px">
        <CardHeader pb={2}>
          <HStack justify="space-between">
            <Heading size="sm">{t('audienceFilter.title')}</Heading>
            {activeCount > 0 && (
              <Button size="xs" variant="ghost" onClick={() => onChange({})}>
                {t('audienceFilter.buttons.clearAll')}
              </Button>
            )}
          </HStack>
        </CardHeader>
        <CardBody pt={0}>
          <Text fontSize="xs" color="gray.500" mb={2}>{t('audienceFilter.presets.intro')}</Text>
          <Wrap spacing={2} mb={4}>
            {PRESETS.map((p) => (
              <WrapItem key={p.id}>
                <Button size="xs" variant="outline"
                  onClick={() => onChange({ ...p.filters })}>
                  {t(p.labelKey)}
                </Button>
              </WrapItem>
            ))}
          </Wrap>

          {/* Live count */}
          <Box p={3} borderWidth="1px" borderRadius="md" bg="blue.50" borderColor="blue.100">
            {countError ? (
              <Alert status="error" borderRadius="md" fontSize="sm" p={2}>
                <AlertIcon />{countError}
              </Alert>
            ) : (
              <HStack spacing={6} align="center" flexWrap="wrap">
                <HStack>
                  {counting
                    ? <Spinner size="sm" color="blue.500" />
                    : <Text fontSize="2xl" fontWeight="bold" color="blue.700">
                        {summary?.deliverable ?? 0}
                      </Text>}
                  <Text fontSize="sm" color="blue.700">
                    {t('audienceFilter.count.willReceive', { count: summary?.deliverable ?? 0 })}
                  </Text>
                </HStack>
                {summary && (
                  <HStack spacing={4} fontSize="xs" color="gray.600">
                    <Text>{t('audienceFilter.count.matched', { count: summary.matched })}</Text>
                    {summary.missingEmail > 0 && (
                      <Tooltip label={t('audienceFilter.count.noEmailTooltip')}>
                        <Text>{t('audienceFilter.count.noEmail', { count: summary.missingEmail })}</Text>
                      </Tooltip>
                    )}
                    {summary.optedOut > 0 && (
                      <Tooltip label={t('audienceFilter.count.unsubscribedTooltip')}>
                        <Text>{t('audienceFilter.count.unsubscribed', { count: summary.optedOut })}</Text>
                      </Tooltip>
                    )}
                  </HStack>
                )}
              </HStack>
            )}
          </Box>
        </CardBody>
      </Card>

      {/* Filters */}
      <Card shadow="sm" borderWidth="1px">
        <CardHeader pb={2}><Heading size="sm">{t('audienceFilter.filtersTitle')}</Heading></CardHeader>
        <CardBody pt={0}>
          <VStack align="stretch" spacing={5}>
            {/* Guest profile */}
            <Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.500"
                textTransform="uppercase" letterSpacing="wider" mb={2}>
                {t('audienceFilter.sections.guestProfile')}
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">{t('audienceFilter.fields.search')}</FormLabel>
                  <Input size="sm" value={f.search || ''}
                    onChange={(e) => set('search', e.target.value)}
                    placeholder={t('audienceFilter.fields.searchPlaceholder')} />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">{t('audienceFilter.fields.nationality')}</FormLabel>
                  <Select size="sm" value=""
                    onChange={(e) => {
                      if (!e.target.value) return;
                      const current = f.nationalities || [];
                      if (!current.includes(e.target.value)) {
                        set('nationalities', [...current, e.target.value]);
                      }
                    }}>
                    <option value="">{t('audienceFilter.fields.addNationality')}</option>
                    {(options?.nationalities || []).map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </Select>
                  <ChipList values={f.nationalities}
                    onRemove={(v) => set('nationalities',
                      (f.nationalities || []).filter((x) => x !== v))} />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">{t('audienceFilter.fields.language')}</FormLabel>
                  <Select size="sm" value=""
                    onChange={(e) => {
                      if (!e.target.value) return;
                      const current = f.languages || [];
                      if (!current.includes(e.target.value)) {
                        set('languages', [...current, e.target.value]);
                      }
                    }}>
                    <option value="">{t('audienceFilter.fields.addLanguage')}</option>
                    {(options?.languages || []).map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </Select>
                  <ChipList values={f.languages}
                    onRemove={(v) => set('languages',
                      (f.languages || []).filter((x) => x !== v))} />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">{t('audienceFilter.fields.birthdayMonth')}</FormLabel>
                  <Select size="sm" value={f.birthdayMonth || ''}
                    onChange={(e) => set('birthdayMonth',
                      e.target.value ? Number(e.target.value) : '')}>
                    <option value="">{t('audienceFilter.fields.anyMonth')}</option>
                    {MONTHS.map((monthKey, i) => (
                      <option key={monthKey} value={i + 1}>{t(monthKey)}</option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">{t('audienceFilter.fields.guestSinceFrom')}</FormLabel>
                  <Input size="sm" type="date" value={f.createdFrom || ''}
                    onChange={(e) => set('createdFrom', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('audienceFilter.fields.guestSinceTo')}</FormLabel>
                  <Input size="sm" type="date" value={f.createdTo || ''}
                    onChange={(e) => set('createdTo', e.target.value)} />
                </FormControl>
              </SimpleGrid>

              <Box mt={3}>
                <FormLabel fontSize="sm" mb={1}>{t('audienceFilter.fields.vipLevel')}</FormLabel>
                <CheckboxGroup
                  value={(f.vipLevels || []).map(String)}
                  onChange={(vals) => set('vipLevels', vals.map(Number))}>
                  <Stack direction="row" spacing={4} flexWrap="wrap">
                    {Object.entries(VIP_LABELS).map(([value, labelKey]) => (
                      <Checkbox key={value} value={value} size="sm">{t(labelKey)}</Checkbox>
                    ))}
                  </Stack>
                </CheckboxGroup>
              </Box>

              <HStack mt={3} spacing={6} flexWrap="wrap">
                <Checkbox size="sm" isChecked={!!f.includeBlacklisted}
                  isDisabled={!!f.onlyBlacklisted}
                  onChange={(e) => set('includeBlacklisted', e.target.checked)}>
                  {t('audienceFilter.fields.includeBlacklisted')}
                </Checkbox>
                <Checkbox size="sm" isChecked={!!f.onlyBlacklisted}
                  onChange={(e) => set('onlyBlacklisted', e.target.checked)}>
                  {t('audienceFilter.fields.onlyBlacklisted')}
                </Checkbox>
              </HStack>
            </Box>

            <Divider />

            {/* Stay history */}
            <Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.500"
                textTransform="uppercase" letterSpacing="wider" mb={2}>
                {t('audienceFilter.sections.stayHistory')}
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">{t('audienceFilter.fields.minStays')}</FormLabel>
                  <Input size="sm" type="number" min={0} value={f.minStays ?? ''}
                    isDisabled={!!f.neverStayed}
                    onChange={(e) => set('minStays', e.target.value === '' ? '' : Number(e.target.value))} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('audienceFilter.fields.maxStays')}</FormLabel>
                  <Input size="sm" type="number" min={0} value={f.maxStays ?? ''}
                    isDisabled={!!f.neverStayed}
                    onChange={(e) => set('maxStays', e.target.value === '' ? '' : Number(e.target.value))} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('audienceFilter.fields.roomType')}</FormLabel>
                  <Select size="sm" value=""
                    onChange={(e) => {
                      if (!e.target.value) return;
                      const id = Number(e.target.value);
                      const current = f.roomTypeIds || [];
                      if (!current.includes(id)) set('roomTypeIds', [...current, id]);
                    }}>
                    <option value="">{t('audienceFilter.fields.addRoomType')}</option>
                    {(options?.roomTypes || []).map((rt) => (
                      <option key={rt.id} value={rt.id}>{rt.name}</option>
                    ))}
                  </Select>
                  <ChipList
                    values={(f.roomTypeIds || []).map((id) =>
                      (options?.roomTypes || []).find((rt) => rt.id === id)?.name || `#${id}`)}
                    onRemove={(label) => {
                      const match = (options?.roomTypes || []).find((rt) => rt.name === label);
                      set('roomTypeIds',
                        (f.roomTypeIds || []).filter((id) => id !== match?.id));
                    }} />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">{t('audienceFilter.fields.lastStayFrom')}</FormLabel>
                  <Input size="sm" type="date" value={f.lastStayFrom || ''}
                    onChange={(e) => set('lastStayFrom', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('audienceFilter.fields.lastStayBefore')}</FormLabel>
                  <Input size="sm" type="date" value={f.lastStayTo || ''}
                    onChange={(e) => set('lastStayTo', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('audienceFilter.fields.bookingSource')}</FormLabel>
                  <Select size="sm" value=""
                    onChange={(e) => {
                      if (!e.target.value) return;
                      const current = f.bookingSources || [];
                      if (!current.includes(e.target.value)) {
                        set('bookingSources', [...current, e.target.value]);
                      }
                    }}>
                    <option value="">{t('audienceFilter.fields.addSource')}</option>
                    {(options?.bookingSources || []).map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </Select>
                  <ChipList values={f.bookingSources}
                    onRemove={(v) => set('bookingSources',
                      (f.bookingSources || []).filter((x) => x !== v))} />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">{t('audienceFilter.fields.stayedBetweenFrom')}</FormLabel>
                  <Input size="sm" type="date" value={f.stayedBetweenFrom || ''}
                    onChange={(e) => set('stayedBetweenFrom', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('audienceFilter.fields.stayedBetweenTo')}</FormLabel>
                  <Input size="sm" type="date" value={f.stayedBetweenTo || ''}
                    onChange={(e) => set('stayedBetweenTo', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('audienceFilter.fields.minTotalSpend')}</FormLabel>
                  <Input size="sm" type="number" min={0} value={f.minTotalSpend ?? ''}
                    onChange={(e) => set('minTotalSpend', e.target.value === '' ? '' : Number(e.target.value))} />
                </FormControl>
              </SimpleGrid>

              <HStack mt={3} spacing={6} flexWrap="wrap">
                <Checkbox size="sm" isChecked={!!f.hasUpcomingStay}
                  onChange={(e) => set('hasUpcomingStay', e.target.checked)}>
                  {t('audienceFilter.presets.upcoming')}
                </Checkbox>
                <Checkbox size="sm" isChecked={!!f.neverStayed}
                  onChange={(e) => set('neverStayed', e.target.checked)}>
                  {t('audienceFilter.fields.neverStayed')}
                </Checkbox>
              </HStack>
            </Box>
          </VStack>
        </CardBody>
      </Card>

      {/* Sample */}
      {showPreviewTable && (
        <Card shadow="sm" borderWidth="1px">
          <CardHeader pb={2}>
            <HStack justify="space-between">
              <Heading size="sm">{t('audienceFilter.sample.title')}</Heading>
              <Badge colorScheme="blue">
                {t('audienceFilter.sample.firstOf', { shown: sample.length, total: summary?.deliverable ?? 0 })}
              </Badge>
            </HStack>
          </CardHeader>
          <CardBody pt={0} px={0}>
            <TableContainer>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>{t('audienceFilter.sample.guest')}</Th><Th>{t('audienceFilter.sample.email')}</Th>
                    <Th>{t('audienceFilter.sample.stays')}</Th><Th>{t('audienceFilter.sample.lastStay')}</Th>
                    <Th isNumeric>{t('audienceFilter.sample.roomSpend')}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sample.map((g) => (
                    <Tr key={g.id}>
                      <Td fontSize="sm">{g.name}</Td>
                      <Td fontSize="sm">{g.email}</Td>
                      <Td fontSize="sm">{g.stayCount ?? 0}</Td>
                      <Td fontSize="sm">{g.lastStayDate || '—'}</Td>
                      <Td fontSize="sm" isNumeric>{(g.totalSpend ?? 0).toFixed(2)}</Td>
                    </Tr>
                  ))}
                  {sample.length === 0 && !counting && (
                    <Tr>
                      <Td colSpan={5} textAlign="center" py={6} color="gray.400">
                        {t('audienceFilter.sample.empty')}
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
};

const ChipList = ({ values, onRemove }) => {
  if (!values || values.length === 0) return null;
  return (
    <Wrap spacing={1} mt={2}>
      {values.map((v) => (
        <WrapItem key={v}>
          <Tag size="sm" colorScheme="blue" borderRadius="full">
            <TagLabel>{v}</TagLabel>
            <TagCloseButton onClick={() => onRemove(v)} />
          </Tag>
        </WrapItem>
      ))}
    </Wrap>
  );
};

export default AudienceFilterBuilder;
